import { DEPLOYMENT_TAG_KEY, DEPLOY_POLICIES, IAM_ROLES_POLICY_NAME, forCloudFormation, iamPolicyVariants, policyDocument, } from './deploy-policy.js';
/**
 * The bootstrap CloudFormation template a customer runs in their own account.
 *
 * This is the trust boundary of the whole product, expressed in about a hundred
 * lines of IAM. It is authored as a typed value rather than a hand-edited YAML
 * file, and pinned by golden fixtures, for the same reason the Terraform config
 * contract is: an accidental change here is a security regression in someone
 * else's account, and it should be a failing test rather than a review someone
 * skims.
 *
 * ## Two roles, deliberately different
 *
 * `QrveyDiagnosticsRole` is read-only and long-lived. It is what support uses day
 * to day, and it is the *only* role the control plane can assume. It cannot read
 * customer data — see the deny statement below, which is the whole point.
 *
 * `QrveyDeployExecRole` is what a run acts as, and assumable **only by the
 * Terraform runner inside the customer's own account**. Since v8 it is scoped
 * to the one deployment each run names (`deploy-policy.ts`), with
 * `AdministratorAccess` kept as a parameter choice. The control plane is not a trusted
 * principal and must never become one: that separation is what stops a
 * compromise of Qrvey's control plane from becoming write access to every
 * customer's infrastructure. The control plane asks the in-account dispatcher to
 * run something; it cannot perform the run itself.
 */
/**
 * Bumped when the template changes in a way the control plane must know about.
 *
 * v8 scopes the deploy role to one deployment per run (IM-50): see
 * `deploy-policy.ts`, and `SCOPED_DEPLOY_BOOTSTRAP_VERSION` for what it asks of
 * the other two stacks.
 *
 * v9 lets that scoped role install and remove a deployment the way the QRV-1135
 * installer builds one (IM-190): roles, policies and Karpenter's instance
 * profile under `/qrvey/`, and Amazon Location's maps and place index.
 */
export const BOOTSTRAP_VERSION = '9';
/**
 * The first access stack whose deploy role can be scoped per deployment.
 *
 * A scoped role — the choice from here, and the default once MP-3630 ships —
 * refuses any session that
 * does not carry a `qrvey-id` session tag — which only the runner wrapper in
 * dispatcher v14 sets, and only a grant v3 runner is permitted to set. So a v8
 * access stack deployed `scoped` makes grant v3 and dispatcher v14 blocking,
 * not merely behind; `onboarding.ts` reads the stack's `DeployPolicy` output to
 * know which it is.
 */
export const SCOPED_DEPLOY_BOOTSTRAP_VERSION = '8';
/** The `DeployPolicy` parameter's values, and what the access stack publishes. */
export const DEPLOY_POLICY_SCOPED = 'scoped';
export const DEPLOY_POLICY_ADMINISTRATOR = 'administrator';
/**
 * The IAM calls by the deploy role that the optional alert reports.
 *
 * 24/7's three policy-version events — `CreatePolicyVersion` with
 * `--set-as-default` is the escalation that rewrites a policy already attached
 * to a passable role — plus the two that put a policy on a role at all. The
 * last two fire on every apply that creates a role, which is the point: a
 * review of what the run attached, not an alarm that something is wrong.
 */
export const IAM_ALERT_EVENTS = [
    'CreatePolicyVersion',
    'SetDefaultPolicyVersion',
    'DeletePolicyVersion',
    'AttachRolePolicy',
    'PutRolePolicy',
];
export const DIAGNOSTICS_ROLE_NAME = 'QrveyDiagnosticsRole';
export const DEPLOY_EXEC_ROLE_NAME = 'QrveyDeployExecRole';
/**
 * The in-account Terraform runner's task role.
 *
 * Created by the dispatcher template (IM-26), not this one. Named here because
 * the deploy execution role's trust policy has to refer to it, and the two must
 * agree.
 */
export const RUNNER_ROLE_NAME = 'QrveyTerraformRunner';
/**
 * Terraform state lives in the customer's own account, as the prototype
 * correctly did. What the prototype lacked was **locking**: it set a state key
 * but provisioned no lock table, so two concurrent applies could corrupt state.
 * Recovering corrupted Terraform state in someone else's account is expensive
 * and manual, which is why versioning below is not optional either.
 */
export const LOCK_TABLE_NAME = 'qrvey-terraform-locks';
/**
 * The lock table's partition key.
 *
 * Terraform's S3 backend requires exactly this attribute name — it writes a
 * conditional put on `LockID` and nothing else. A table with any other key
 * schema is accepted at bootstrap and then **silently fails to lock**, which is
 * the failure this story exists to prevent, so it is asserted rather than
 * assumed.
 */
export const LOCK_TABLE_KEY = 'LockID';
/**
 * The Terraform state bucket's name, which is also how Qrvey finds a region.
 *
 * Account- and region-qualified because S3 bucket names are globally unique —
 * and that qualification turns out to carry more weight than uniqueness. Since
 * IM-95 it is how the control plane discovers **which regions an account is set
 * up in**: one `s3:ListAllMyBuckets` call, filtered by this prefix, and the
 * region falls out of the name.
 *
 * That matters because the alternative was a `DescribeStacks` per candidate
 * region — a call per region on a screen already capped at two. One global list
 * costs the same whether a customer has one region or ten.
 *
 * So the shape of this name is load-bearing in two places, and it used to be a
 * literal written out in each template. `regionFromStateBucket` is its inverse,
 * and a test round-trips them so the two cannot drift apart.
 */
export const STATE_BUCKET_PREFIX = 'qrvey-tfstate';
/**
 * The bucket one account's state lives in, in one region.
 *
 * The third caller of this shape, and the reason it is a function rather than a
 * third literal: the templates create the bucket, `regionFromStateBucket` reads
 * a region back out of the name, and the console names the object a withheld
 * secret can be read from. A name assembled by hand in any one of those places
 * is a path that looks right and points nowhere.
 */
export const stateBucketFor = (awsAccountId, region) => `${STATE_BUCKET_PREFIX}-${awsAccountId}-${region}`;
/** As CloudFormation writes it, for a template that creates the bucket. */
export const STATE_BUCKET_NAME_SUB = stateBucketFor('${AWS::AccountId}', '${AWS::Region}');
/**
 * Which region a state bucket belongs to, or undefined if it is not one of ours.
 *
 * Deliberately strict about the account id as well as the prefix: a customer's
 * own bucket called `qrvey-tfstate-something` should not be read as a region
 * Qrvey set up, and `ListAllMyBuckets` returns every bucket they have.
 */
export const regionFromStateBucket = (bucketName, awsAccountId) => {
    const expected = `${STATE_BUCKET_PREFIX}-${awsAccountId}-`;
    if (!bucketName.startsWith(expected))
        return undefined;
    const region = bucketName.slice(expected.length);
    // The same shape `regionProblem` accepts, restated rather than imported to
    // keep this module free of a dependency on the deployment contract.
    return /^[a-z]{2}(-[a-z]+)+-\d{1,2}$/.test(region) ? region : undefined;
};
/** Run logs, in the customer's account. Their record of what was done. */
export const RUN_LOG_GROUP = '/qrvey/terraform-runs';
/** Bounded so versioning does not grow without limit; long enough to recover. */
export const STATE_VERSION_RETENTION_DAYS = 90;
export const RUN_LOG_RETENTION_DAYS = 90;
const sub = (template) => ({ 'Fn::Sub': template });
/**
 * Actions that read customer data rather than describe infrastructure.
 *
 * Denied outright on the diagnostics role. This matters more than the allow list
 * above it: AWS's own `ReadOnlyAccess` policy would have been one line and grants
 * `s3:GetObject`, `dynamodb:Scan` and `secretsmanager:GetSecretValue` — that is
 * read access to the customer's data, which is exactly what this product
 * promises never to touch. An explicit deny cannot be widened by a later
 * accidental allow, since deny always wins.
 *
 * Almost none of these is in the allow list, so today each is refused twice.
 * The deny is for the day somebody widens that list, and it is the half a
 * customer's security team can check by reading: every way into their objects,
 * items, rows, secrets and messages, named. IM-51 widened it from the fastest
 * leaks to every read path in each of those categories — PartiQL beside
 * `GetItem`, the batch secret read beside the single one, the database
 * credential issuers beside the query APIs — because an allow a year from now
 * will not be for the obvious action.
 *
 * `docs/data-access.md` lists the same actions for customers, and a test fails
 * if the two drift.
 */
export const DATA_PLANE_ACTIONS = [
    // Objects.
    's3:GetObject*',
    's3-object-lambda:GetObject*',
    // Table items, by key, by scan, by PartiQL and from the stream.
    'dynamodb:GetItem',
    'dynamodb:BatchGetItem',
    'dynamodb:Query',
    'dynamodb:Scan',
    'dynamodb:PartiQLSelect',
    'dynamodb:ExecuteStatement',
    'dynamodb:BatchExecuteStatement',
    'dynamodb:GetRecords',
    // Rows: the query APIs, and the calls that mint credentials to run queries.
    'rds-data:ExecuteStatement',
    'rds-data:BatchExecuteStatement',
    'rds-db:connect',
    'redshift:GetClusterCredentials',
    'redshift:GetClusterCredentialsWithIAM',
    'redshift-serverless:GetCredentials',
    'redshift-data:GetStatementResult',
    'athena:GetQueryResults',
    // Search indexes and caches.
    'es:ESHttp*',
    'aoss:APIAccessAll',
    'elasticache:Connect',
    // Secrets.
    'secretsmanager:GetSecretValue',
    'secretsmanager:BatchGetSecretValue',
    // Decryption with any key, customer-managed or not.
    'kms:Decrypt',
    // Messages in flight.
    'sqs:ReceiveMessage',
    'kinesis:GetRecords',
];
/**
 * Reads that look like metadata and return customer data in the response
 * (IM-51).
 *
 * The allow list is wildcarded by verb — `lambda:Get*`, `ec2:Describe*` — and
 * each of these sits inside one of those wildcards. They were all permitted
 * until bootstrap v7, which is the real gap IM-51 found: the data-plane deny
 * above named the obvious reads, and these are the ones nobody thinks of as
 * reads.
 *
 * - A function's and a task's **environment variables**, returned in plain
 *   text, which is where applications keep API keys and connection strings.
 *   `GetFunction` also returns a download link for the function's code.
 * - An instance's **user data**, from the instance and from its launch template
 *   — the bootstrap script, which routinely carries a secret.
 * - A stack's **template**, which a customer's own stacks may have secrets
 *   written into. `DescribeStacks` stays: it masks `NoEcho` parameters, and the
 *   onboarding screen is built on it.
 * - **Object names**, from `ListBucket`. A key is often a filename, and a
 *   filename is often data. Nothing here lists objects; bucket names come from
 *   `ListAllMyBuckets`.
 * - Windows **passwords** and **console output**, neither of which describes
 *   anything.
 */
export const DATA_BEARING_READS = [
    'lambda:GetFunction',
    'lambda:GetFunctionConfiguration',
    'ecs:DescribeTaskDefinition',
    'ec2:DescribeInstanceAttribute',
    'ec2:DescribeLaunchTemplateVersions',
    'ec2:GetPasswordData',
    'ec2:GetConsoleOutput',
    'cloudformation:GetTemplate',
    's3:ListBucket',
    's3:ListBucketVersions',
];
/**
 * Log reads, denied everywhere **except** the run log group (IM-51).
 *
 * The allow list carried `logs:GetLogEvents` and `logs:FilterLogEvents` on
 * `*`, which is every log group in the account — the customer's own
 * application logs included — while every document said Qrvey reads the run
 * logs and nothing else. The control plane only ever reads
 * `/qrvey/terraform-runs`, whose lines are redacted in the customer's account
 * before they are written, so this makes the documents true rather than
 * making the product smaller. Same shape as the parameter deny below.
 *
 * Logs Insights and live tail are denied outright: neither is scoped to one
 * log group the way these two are, and nothing here uses them.
 */
export const LOG_READ_ACTIONS = [
    'logs:GetLogEvents',
    'logs:FilterLogEvents',
    'logs:StartQuery',
    'logs:GetQueryResults',
    'logs:GetLogRecord',
    'logs:StartLiveTail',
    'logs:Unmask',
];
/**
 * Parameter Store reads, denied everywhere **except** our own namespace.
 *
 * Split out of the blanket deny above because the two are not the same question.
 * A customer's own parameters routinely hold connection strings and API keys, so
 * reading them is reading customer data. The values *we* publish under
 * `/qrvey/` — the bootstrap version, the state backend — are ours, and the
 * control plane has to read them to know what it is talking to.
 *
 * This was a real bug: IM-24 denied `ssm:GetParameter` outright and IM-25 then
 * published the bootstrap version to SSM. Nothing failed, because nothing read
 * it until IM-27. Deny always wins, so no later allow could have fixed it.
 */
export const PARAMETER_READ_ACTIONS = [
    'ssm:GetParameter',
    'ssm:GetParameters',
    'ssm:GetParametersByPath',
    // Returns every past value, which is the same read with a longer memory.
    'ssm:GetParameterHistory',
];
/**
 * What support may actually do.
 *
 * Describe and list only, and only the services a Qrvey instance is built from.
 * Read-only is not the same as harmless, so this is an allow list rather than
 * `ReadOnlyAccess` minus exceptions — a new AWS service is then invisible to
 * support until someone adds it deliberately, which is the safer default.
 */
export const DIAGNOSTICS_ACTIONS = [
    /*
     * What the account spends (IM-130).
     *
     * Cost Explorer reports what AWS billed, aggregated — it reads no resource
     * and can return no customer data, which is why it sits in the allow list
     * rather than needing a carve-out from the data-plane deny the way
     * `ssm:GetParameter` did.
     *
     * It is still commercially sensitive, so it is its own capability on this
     * side (`cost:read`) rather than falling out of `deployment:read`, and every
     * read lands in the customer's own CloudTrail attributed by session name like
     * everything else.
     *
     * `ce:GetCostAndUsage` alone, not `ce:Get*` or `ce:*`: the rest of that
     * namespace includes anomaly monitors, budgets and savings-plan purchase
     * recommendations, none of which this reads and one of which writes.
     */
    'ce:GetCostAndUsage',
    'cloudformation:Describe*',
    'cloudformation:Get*',
    'cloudformation:List*',
    'cloudwatch:Describe*',
    'cloudwatch:Get*',
    'cloudwatch:List*',
    'dynamodb:DescribeTable',
    'dynamodb:ListTables',
    'ec2:Describe*',
    'ecs:Describe*',
    'ecs:List*',
    'eks:Describe*',
    'eks:List*',
    'elasticache:Describe*',
    'elasticloadbalancing:Describe*',
    'es:Describe*',
    'es:List*',
    'iam:Get*',
    'iam:List*',
    'lambda:Get*',
    'lambda:List*',
    'logs:Describe*',
    'logs:FilterLogEvents',
    'logs:GetLogEvents',
    'rds:Describe*',
    'rds:List*',
    's3:GetBucketLocation',
    's3:GetBucketPolicy',
    's3:GetBucketTagging',
    's3:GetEncryptionConfiguration',
    's3:ListAllMyBuckets',
    'sqs:GetQueueAttributes',
    'sqs:ListQueues',
    'ssm:DescribeParameters',
    // Permitted broadly here and narrowed by the deny above to /qrvey/* only.
    'ssm:GetParameter',
    'ssm:GetParameters',
    'ssm:GetParametersByPath',
    'sts:GetCallerIdentity',
];
export const buildBootstrapTemplate = (input) => {
    const externalIdParameter = {
        Type: 'String',
        Description: 'Generated by Qrvey and shown when this account was registered. Do not invent a value.',
        MinLength: 2,
        MaxLength: 1224,
        AllowedPattern: '[\\w+=,.@:/-]+',
        // Hides the value in the console, in stack events and in describe-stacks
        // output. A shared secret has no business appearing in either.
        NoEcho: true,
        ...(input.externalId === undefined ? {} : { Default: input.externalId }),
    };
    return {
        AWSTemplateFormatVersion: '2010-09-09',
        Description: `Qrvey Instance Manager access for this account (bootstrap v${BOOTSTRAP_VERSION})`,
        Metadata: {
            'AWS::CloudFormation::Interface': {
                ParameterGroups: [
                    {
                        Label: { default: 'Qrvey access' },
                        Parameters: ['ExternalId', 'ControlPlaneAccountId'],
                    },
                    {
                        Label: { default: 'Networking (optional, for private cluster endpoints)' },
                        Parameters: ['VpcId', 'SubnetIds'],
                    },
                    {
                        Label: { default: 'What a Qrvey run may change in this account' },
                        Parameters: ['DeployPolicy', 'DeployRoleBoundaryArn', 'AlertEmail'],
                    },
                ],
            },
        },
        Parameters: {
            ExternalId: externalIdParameter,
            ControlPlaneAccountId: {
                Type: 'String',
                Description: "Qrvey's account. Only this account may assume the diagnostics role.",
                AllowedPattern: '\\d{12}',
                Default: input.controlPlaneAccountId,
            },
            /*
             * `administrator` is the default until the installer tags the load
             * balancers its cluster creates (MP-3630), and `scoped` is the choice to
             * make once it does. Measured on the first scoped destroy (2026-09-29):
             * the Kong gateway's NLB names its deployment only in a tag *key*, which
             * no IAM condition can read, so a scoped destroy stops at it and someone
             * has to delete it by hand. Scoped plans and applies are unaffected.
             * Switching the default back is this one line.
             *
             * `administrator` is also the documented fallback — the policy every
             * account had until v8, kept as a choice a customer makes knowingly
             * rather than removed, because an installer path nobody
             * has measured yet (an upgrade, an existing VPC, a feature flag) may need
             * something the scoped set refuses, and the answer to that should not be
             * "wait for a release". `docs/deploy-permissions.md` says what each one
             * means.
             */
            DeployPolicy: {
                Type: 'String',
                Description: 'scoped: each Qrvey run may change only its own deployment; with an installer before 9.6 a destroy needs the gateway load balancer deleted by hand. administrator: AdministratorAccess, as before bootstrap v8.',
                AllowedValues: [DEPLOY_POLICY_SCOPED, DEPLOY_POLICY_ADMINISTRATOR],
                Default: DEPLOY_POLICY_ADMINISTRATOR,
            },
            /*
             * Empty until the installer sets a permissions boundary on the roles it
             * creates (MP-3619). Once it does, naming the boundary here makes it a
             * condition of every role the run creates or attaches to — the one thing
             * that closes the IAM residual. Setting it before the image does refuses
             * every role the installer creates.
             */
            DeployRoleBoundaryArn: {
                Type: 'String',
                Description: 'Optional. A permissions boundary the deploy role must set on every IAM role it creates. Leave empty until Qrvey confirms the installer sets one.',
                Default: '',
                AllowedPattern: '(arn:aws[a-z-]*:iam::\\d{12}:policy/[\\w+=,.@/-]+)?',
            },
            AlertEmail: {
                Type: 'String',
                Description: 'Optional. An address to email when the deploy role attaches or rewrites an IAM policy. Takes effect only in us-east-1, where IAM events are delivered.',
                Default: '',
                AllowedPattern: '([^@\\s]+@[^@\\s]+)?',
            },
        },
        Conditions: {
            ScopedDeployRole: { 'Fn::Equals': [{ Ref: 'DeployPolicy' }, DEPLOY_POLICY_SCOPED] },
            RequireDeployRoleBoundary: {
                'Fn::Not': [{ 'Fn::Equals': [{ Ref: 'DeployRoleBoundaryArn' }, ''] }],
            },
            IamChangeAlertRequested: { 'Fn::Not': [{ 'Fn::Equals': [{ Ref: 'AlertEmail' }, ''] }] },
            /*
             * IAM is global and its CloudTrail events reach EventBridge in us-east-1
             * only, so a rule anywhere else would be created, look configured, and
             * never fire. Not created elsewhere, and the output says so.
             */
            IamChangeAlert: {
                'Fn::And': [
                    { Condition: 'IamChangeAlertRequested' },
                    { 'Fn::Equals': [{ Ref: 'AWS::Region' }, 'us-east-1'] },
                ],
            },
        },
        Resources: {
            DiagnosticsRole: {
                Type: 'AWS::IAM::Role',
                Properties: {
                    RoleName: DIAGNOSTICS_ROLE_NAME,
                    Description: 'Read-only access for Qrvey support. Cannot read customer data.',
                    // One hour. Support sessions are interactive; a longer window is a
                    // longer-lived credential for no benefit.
                    MaxSessionDuration: 3600,
                    AssumeRolePolicyDocument: {
                        Version: '2012-10-17',
                        Statement: [
                            {
                                Effect: 'Allow',
                                // The account root as principal, narrowed by the external ID.
                                // Naming a specific Qrvey role here would couple this template
                                // to Qrvey's internal role naming, and every rename would need
                                // every customer to redeploy.
                                Principal: { AWS: sub('arn:${AWS::Partition}:iam::${ControlPlaneAccountId}:root') },
                                Action: 'sts:AssumeRole',
                                Condition: { StringEquals: { 'sts:ExternalId': { Ref: 'ExternalId' } } },
                            },
                        ],
                    },
                    Policies: [
                        {
                            PolicyName: 'QrveyDiagnosticsRead',
                            PolicyDocument: {
                                Version: '2012-10-17',
                                Statement: [
                                    {
                                        Sid: 'DescribeInfrastructure',
                                        Effect: 'Allow',
                                        Action: [...DIAGNOSTICS_ACTIONS],
                                        Resource: '*',
                                    },
                                    {
                                        Sid: 'NeverReadCustomerData',
                                        Effect: 'Deny',
                                        Action: [...DATA_PLANE_ACTIONS],
                                        Resource: '*',
                                    },
                                    {
                                        Sid: 'NeverReadDataInsideDescriptions',
                                        Effect: 'Deny',
                                        Action: [...DATA_BEARING_READS],
                                        Resource: '*',
                                    },
                                    {
                                        // The run log group in every region, and nothing else.
                                        // Both forms: GetLogEvents authorizes on the stream, which
                                        // sits under the group's ARN.
                                        Sid: 'NeverReadCustomerLogs',
                                        Effect: 'Deny',
                                        Action: [...LOG_READ_ACTIONS],
                                        NotResource: [
                                            sub(`arn:\${AWS::Partition}:logs:*:\${AWS::AccountId}:log-group:${RUN_LOG_GROUP}`),
                                            sub(`arn:\${AWS::Partition}:logs:*:\${AWS::AccountId}:log-group:${RUN_LOG_GROUP}:*`),
                                        ],
                                    },
                                    {
                                        // Deny with NotResource: everything except our own
                                        // namespace. Reading /qrvey/* is reading our own published
                                        // facts; reading anything else is reading customer data.
                                        Sid: 'NeverReadCustomerParameters',
                                        Effect: 'Deny',
                                        Action: [...PARAMETER_READ_ACTIONS],
                                        NotResource: sub('arn:${AWS::Partition}:ssm:*:${AWS::AccountId}:parameter/qrvey/*'),
                                    },
                                ],
                            },
                        },
                    ],
                },
            },
            DeployExecRole: {
                Type: 'AWS::IAM::Role',
                Properties: {
                    RoleName: DEPLOY_EXEC_ROLE_NAME,
                    Description: 'Used by the in-account Terraform runner. Qrvey cannot assume this role.',
                    // Terraform applies for a full instance run long; a session that
                    // expires mid-apply leaves half-created infrastructure.
                    MaxSessionDuration: 14400,
                    AssumeRolePolicyDocument: {
                        Version: '2012-10-17',
                        Statement: [
                            {
                                Sid: 'OnlyTheInAccountRunner',
                                Effect: 'Allow',
                                /**
                                 * The *customer's* own account, narrowed by `aws:PrincipalArn`
                                 * to exactly the runner role.
                                 *
                                 * Naming the runner role ARN directly as the principal would be
                                 * equivalent but will not deploy: IAM validates principals and
                                 * rejects an ARN for a role that does not exist yet, and the
                                 * runner is created later by the dispatcher template. Root plus
                                 * a PrincipalArn condition is the standard way around that, and
                                 * it also survives the runner role being recreated.
                                 *
                                 * Note what is absent: Qrvey's account. It must stay absent.
                                 *
                                 * Also absent, since v5: an `sts:ExternalId` condition. An
                                 * external id is confused-deputy protection for a *cross*-account
                                 * trust — it is what stops someone who learns a customer's role
                                 * ARN from inducing Qrvey to assume it on their behalf. That is a
                                 * real threat against `QrveyDiagnosticsRole`, whose trusted
                                 * principal is Qrvey's account, and it is why that role still
                                 * carries one.
                                 *
                                 * This role is different in shape. It is trusted only by
                                 * principals inside the customer's *own* account, narrowed to a
                                 * single role ARN, so there is no deputy to confuse. Exploiting
                                 * the absence means being able to create or modify
                                 * `QrveyTerraformRunner` in that account — at which point you are
                                 * an administrator there and a shared secret protects nothing.
                                 * It was the diagnostics role's requirement copied onto a role
                                 * with a different threat model, and it cost a mandatory NoEcho
                                 * field on two more templates plus a secret in every dispatch.
                                 *
                                 * What gates this role is unchanged, and is where it belongs: the
                                 * runner's own identity policy may assume it only before the
                                 * grant expiry, enforced by AWS at `AssumeRole`.
                                 */
                                Principal: { AWS: sub('arn:${AWS::Partition}:iam::${AWS::AccountId}:root') },
                                Action: 'sts:AssumeRole',
                                Condition: {
                                    ArnEquals: {
                                        'aws:PrincipalArn': sub(`arn:\${AWS::Partition}:iam::\${AWS::AccountId}:role/${RUNNER_ROLE_NAME}`),
                                    },
                                },
                            },
                            {
                                /**
                                 * The runner may tag its session with the deployment it runs
                                 * for, and with nothing else (v8, IM-50).
                                 *
                                 * The tag is what the scoped policies pin every statement to,
                                 * so which keys may be set is the whole question: exactly
                                 * `qrvey-id`, and it must be present. The same principal shape
                                 * as the statement above, for the same reason — the account
                                 * root delegates the decision to the runner's own identity
                                 * policy, where grant v3 allows `sts:TagSession` under the same
                                 * expiry as the assume-role. A trust naming the runner directly
                                 * would let it tag after the grant lapsed.
                                 *
                                 * Separate from the assume-role statement rather than folded
                                 * into it, so an untagged assume-role — every dispatcher before
                                 * v14 — is still admitted, and meets `DenyWithoutDeploymentTag`
                                 * in a scoped policy rather than a trust refusal nobody can
                                 * read.
                                 */
                                Sid: 'RunnerTagsItsDeployment',
                                Effect: 'Allow',
                                Principal: { AWS: sub('arn:${AWS::Partition}:iam::${AWS::AccountId}:root') },
                                Action: 'sts:TagSession',
                                Condition: {
                                    ArnEquals: {
                                        'aws:PrincipalArn': sub(`arn:\${AWS::Partition}:iam::\${AWS::AccountId}:role/${RUNNER_ROLE_NAME}`),
                                    },
                                    'ForAllValues:StringEquals': { 'aws:TagKeys': [DEPLOYMENT_TAG_KEY] },
                                    Null: { [`aws:RequestTag/${DEPLOYMENT_TAG_KEY}`]: 'false' },
                                },
                            },
                        ],
                    },
                    /*
                     * `AdministratorAccess` only when the customer chose it. The scoped
                     * set is the managed policies below, which attach themselves to this
                     * role — so in scoped mode the role carries nothing of its own and
                     * everything it may do is in `QrveyDeploy-*`, readable by name.
                     */
                    ManagedPolicyArns: {
                        'Fn::If': [
                            'ScopedDeployRole',
                            { Ref: 'AWS::NoValue' },
                            [sub('arn:${AWS::Partition}:iam::aws:policy/AdministratorAccess')],
                        ],
                    },
                },
            },
            ...deployPolicyResources(),
            ...iamChangeAlertResources(),
            /**
             * Terraform state.
             *
             * `DeletionPolicy: Retain` because deleting this bucket destroys the state
             * for every deployment in the account, which orphans the infrastructure it
             * describes — Terraform would no longer know the resources exist, and they
             * would have to be imported or deleted by hand. Retaining it does mean a
             * re-bootstrap after a stack delete hits a name conflict; that is the
             * cheaper of the two problems by a wide margin.
             */
        },
        Outputs: {
            DiagnosticsRoleArn: {
                Description: 'Give this to Qrvey to complete onboarding',
                Value: { 'Fn::GetAtt': ['DiagnosticsRole', 'Arn'] },
            },
            DeployExecRoleArn: {
                Description: 'Used by the in-account runner only',
                Value: { 'Fn::GetAtt': ['DeployExecRole', 'Arn'] },
            },
            BootstrapVersion: { Value: BOOTSTRAP_VERSION },
            /*
             * What the deploy role may do, for the onboarding screen: a scoped role
             * refuses every run from a dispatcher older than v14 or a grant older
             * than v3, and only this output can tell the console which one it is
             * looking at.
             */
            DeployPolicy: { Value: { Ref: 'DeployPolicy' } },
            IamChangeAlert: {
                Value: {
                    'Fn::If': [
                        'IamChangeAlert',
                        'enabled',
                        {
                            'Fn::If': [
                                'IamChangeAlertRequested',
                                'not created: IAM events are delivered only in us-east-1; deploy this stack there',
                                'not requested',
                            ],
                        },
                    ],
                },
            },
            // Deliberately no external ID output. Outputs are readable by anyone with
            // cloudformation:DescribeStacks, which defeats NoEcho on the parameter.
        },
    };
};
/**
 * The scoped policy set, as managed policies that attach themselves to the
 * deploy role — created only when `DeployPolicy` is `scoped`.
 *
 * Managed rather than inline because an inline policy counts against one
 * 10,240-character budget for the role as a whole, and this set is five times
 * that; eight managed policies of at most 6,144 each is the shape IAM allows.
 * Fixed names, like the roles: this stack is once per account, and a name is
 * what `DenyChangingQrveyAccess` protects them by.
 */
const deployPolicyResources = () => {
    const resources = {};
    for (const policy of DEPLOY_POLICIES) {
        const logicalId = policy.name.replace(/[^A-Za-z0-9]/g, '');
        const document = policy.name === IAM_ROLES_POLICY_NAME
            ? (() => {
                const variants = iamPolicyVariants({ Ref: 'DeployRoleBoundaryArn' });
                return { 'Fn::If': ['RequireDeployRoleBoundary', variants.bounded, variants.unbounded] };
            })()
            : forCloudFormation(policyDocument(policy.statements));
        resources[logicalId] = {
            Type: 'AWS::IAM::ManagedPolicy',
            Condition: 'ScopedDeployRole',
            Properties: {
                ManagedPolicyName: policy.name,
                Description: policy.description,
                Roles: [{ Ref: 'DeployExecRole' }],
                PolicyDocument: document,
            },
        };
    }
    return resources;
};
/**
 * The optional alert on IAM changes by the deploy role (24/7's
 * `cloudtrail-iam-policy-alert.md`, Option A), created only when `AlertEmail`
 * is given and the stack is in us-east-1.
 *
 * 24/7 kept it out of Qrvey's policies on the argument that the monitored party
 * should not own the alarm. That still holds: this is a stack the customer
 * deploys in their own account, the topic and its subscription are theirs, and
 * the deploy role can touch neither — nothing in the scoped set names SNS
 * topics or rules outside the deployment's own.
 */
const iamChangeAlertResources = () => ({
    IamChangeAlertTopic: {
        Type: 'AWS::SNS::Topic',
        Condition: 'IamChangeAlert',
        Properties: { DisplayName: 'Qrvey deploy role changed IAM' },
    },
    IamChangeAlertSubscription: {
        Type: 'AWS::SNS::Subscription',
        Condition: 'IamChangeAlert',
        Properties: {
            TopicArn: { Ref: 'IamChangeAlertTopic' },
            Protocol: 'email',
            Endpoint: { Ref: 'AlertEmail' },
        },
    },
    IamChangeAlertRule: {
        Type: 'AWS::Events::Rule',
        Condition: 'IamChangeAlert',
        Properties: {
            Description: 'IAM policy changes made by QrveyDeployExecRole',
            EventPattern: {
                source: ['aws.iam'],
                'detail-type': ['AWS API Call via CloudTrail'],
                detail: {
                    eventSource: ['iam.amazonaws.com'],
                    eventName: [...IAM_ALERT_EVENTS],
                    // The role, not a session: every run is a different session name.
                    userIdentity: {
                        sessionContext: {
                            sessionIssuer: { arn: [{ 'Fn::GetAtt': ['DeployExecRole', 'Arn'] }] },
                        },
                    },
                },
            },
            Targets: [{ Id: 'email', Arn: { Ref: 'IamChangeAlertTopic' } }],
        },
    },
    IamChangeAlertTopicPolicy: {
        Type: 'AWS::SNS::TopicPolicy',
        Condition: 'IamChangeAlert',
        Properties: {
            Topics: [{ Ref: 'IamChangeAlertTopic' }],
            PolicyDocument: {
                Version: '2012-10-17',
                Statement: [
                    {
                        Effect: 'Allow',
                        Principal: { Service: 'events.amazonaws.com' },
                        Action: 'sns:Publish',
                        Resource: { Ref: 'IamChangeAlertTopic' },
                        Condition: {
                            ArnEquals: { 'aws:SourceArn': { 'Fn::GetAtt': ['IamChangeAlertRule', 'Arn'] } },
                        },
                    },
                ],
            },
        },
    },
});
//# sourceMappingURL=template.js.map